import { WASQLiteVFS } from '@powersync/web';

/**
 * Choice of SQLite virtual file system (docs/07 §7, ADR-0012). The preferred VFS comes from
 * `VITE_SQLITE_VFS` so the persistence matrix can be run without code changes. OPFS variants are
 * unusable in Safari private windows: when OPFS is not writable the app falls back to memory and
 * warns that nothing will persist.
 */

export type VfsName =
  | 'OPFSCoopSyncVFS'
  | 'OPFSWriteAheadVFS'
  | 'IDBBatchAtomicVFS'
  | 'AccessHandlePoolVFS'
  | 'InMemoryVfs';

export interface VfsChoice {
  name: VfsName;
  vfs: WASQLiteVFS;
  /** True when the preferred VFS was not available and memory is used instead. */
  fallback: boolean;
}

const VFS_BY_NAME: Record<VfsName, WASQLiteVFS> = {
  OPFSCoopSyncVFS: WASQLiteVFS.OPFSCoopSyncVFS,
  // Concurrent readers in extra workers (@powersync/web ≥ 2.3; evaluated in f1-local-write-latency).
  OPFSWriteAheadVFS: WASQLiteVFS.OPFSWriteAheadVFS,
  IDBBatchAtomicVFS: WASQLiteVFS.IDBBatchAtomicVFS,
  AccessHandlePoolVFS: WASQLiteVFS.AccessHandlePoolVFS,
  InMemoryVfs: WASQLiteVFS.InMemoryVfs,
};

/**
 * Whether the origin private file system is usable (false in Safari private windows, where
 * `getDirectory()` throws). Safari only writes OPFS files from workers (sync access handles), so
 * the probe creates and removes a file instead of writing to it.
 */
export async function opfsWritable(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return false;
    const root = await navigator.storage.getDirectory();
    await root.getFileHandle('.flow-probe', { create: true });
    await root.removeEntry('.flow-probe').catch(() => undefined);
    return true;
  } catch {
    // Safari private windows (and WebKit ephemeral contexts): getDirectory() throws UnknownError.
    return false;
  }
}

/** Whether IndexedDB can be opened (it is unavailable in some private windows). */
export function indexedDbAvailable(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(false);
        return;
      }
      const request = indexedDB.open('flow-probe');
      request.onerror = () => resolve(false);
      request.onsuccess = () => {
        request.result.close();
        indexedDB.deleteDatabase('flow-probe');
        resolve(true);
      };
    } catch {
      resolve(false);
    }
  });
}

export async function chooseVfs(preferred: VfsName): Promise<VfsChoice> {
  const usesOpfs =
    preferred === 'OPFSCoopSyncVFS' ||
    preferred === 'OPFSWriteAheadVFS' ||
    preferred === 'AccessHandlePoolVFS';
  const available = usesOpfs
    ? await opfsWritable()
    : preferred === 'IDBBatchAtomicVFS'
      ? await indexedDbAvailable()
      : true;
  if (available) {
    return { name: preferred, vfs: VFS_BY_NAME[preferred], fallback: false };
  }
  return { name: 'InMemoryVfs', vfs: WASQLiteVFS.InMemoryVfs, fallback: true };
}
